import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Drawer, Empty, Icon, message, Modal, Popover, Tag, Spin, Alert, Form, Input } from "antd";
import moment from "moment";

import { request } from "~/components/Instance";
import { supabase } from "~/lib/supabase";

// Instagram Login uses a separate Instagram App ID, distinct from the Facebook
// App ID. We prefer REACT_APP_META_IG_APP_ID and fall back to REACT_APP_META_APP_ID
// so older deploys don't break before the new var is set.
var META_APP_ID = (typeof process !== 'undefined' && process.env &&
    (process.env.REACT_APP_META_IG_APP_ID || process.env.REACT_APP_META_APP_ID)) || '';
var SUPABASE_URL = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SUPABASE_URL) || '';
var SUPABASE_ANON_KEY = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_SUPABASE_ANON_KEY) || '';

var IG_SCOPES = [
    'instagram_business_basic',
    'instagram_business_manage_messages',
].join(',');

function fetchSupabaseToken() {
    return request('GET', '/supabase-token', null, false).then(function(res) {
        return res && res.data && res.data.token;
    });
}

/**
 * Full Instagram integration settings page — lives at /configuracoes/integracoes/instagram.
 * Shows connection status, prerequisites, scopes, and the connect/disconnect actions.
 */
var InstagramIntegration = function() {
    var [loading, setLoading] = useState(true);
    var [connection, setConnection] = useState(null);
    var [connecting, setConnecting] = useState(false);
    var [manualOpen, setManualOpen] = useState(false);
    var [manualSubmitting, setManualSubmitting] = useState(false);
    var [manualForm, setManualForm] = useState({ page_id: '', page_access_token: '', instagram_business_id: '', username: '' });
    var [messagesOpen, setMessagesOpen] = useState(false);
    var [messagesLoading, setMessagesLoading] = useState(false);
    var [messages, setMessages] = useState([]);

    var loadMessages = useCallback(function() {
        setMessagesLoading(true);
        fetchSupabaseToken()
            .then(function(token) {
                if (!token) throw new Error('no-token');
                return fetch(
                    SUPABASE_URL + '/rest/v1/instagram_messages?select=id,direction,text,sender_username,sender_id,attachments,received_at&order=received_at.desc&limit=100',
                    {
                        headers: {
                            'Authorization': 'Bearer ' + token,
                            'apikey': SUPABASE_ANON_KEY,
                        },
                    }
                ).then(function(r) { return r.ok ? r.json() : []; });
            })
            .then(function(rows) {
                setMessages(Array.isArray(rows) ? rows : []);
            })
            .catch(function() { setMessages([]); })
            .finally(function() { setMessagesLoading(false); });
    }, []);

    var openMessages = useCallback(function() {
        setMessagesOpen(true);
        loadMessages();
    }, [loadMessages]);

    var loadStatus = useCallback(function() {
        setLoading(true);
        fetchSupabaseToken()
            .then(function(token) {
                if (!token) throw new Error('no-token');
                return fetch(SUPABASE_URL + '/rest/v1/instagram_connections?is_active=eq.true&select=page_id,username,subscribed_at,last_event_at', {
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'apikey': SUPABASE_ANON_KEY,
                    },
                }).then(function(r) { return r.ok ? r.json() : []; });
            })
            .then(function(rows) {
                setConnection(Array.isArray(rows) && rows.length ? rows[0] : null);
            })
            .catch(function() { setConnection(null); })
            .finally(function() { setLoading(false); });
    }, []);

    useEffect(function() { loadStatus(); }, [loadStatus]);

    useEffect(function() {
        var params = new URLSearchParams(window.location.search);
        var status = params.get('ig_status');
        var err = params.get('ig_error');
        if (!status && !err) return;
        if (status === 'success') {
            message.success('Instagram conectado com sucesso!');
            loadStatus();
        } else if (err) {
            message.error(decodeURIComponent(err) || 'Erro ao conectar o Instagram');
        }
        if (window.history && window.history.replaceState) {
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [loadStatus]);

    var handleConnect = useCallback(function() {
        if (!META_APP_ID) {
            Modal.error({
                title: 'Configuração pendente',
                content: 'A integração com Instagram ainda não foi configurada (META_APP_ID ausente). Contate o suporte.',
            });
            return;
        }
        var redirectUri = window.location.origin + '/oauth/instagram';
        var authUrl = 'https://www.instagram.com/oauth/authorize'
            + '?client_id=' + encodeURIComponent(META_APP_ID)
            + '&redirect_uri=' + encodeURIComponent(redirectUri)
            + '&scope=' + encodeURIComponent(IG_SCOPES)
            + '&response_type=code'
            + '&state=instagram_connect';

        var popup = window.open(authUrl, 'ig-oauth', 'width=640,height=720');
        setConnecting(true);

        var finish = function() {
            setConnecting(false);
            window.removeEventListener('message', messageHandler);
            clearInterval(poll);
        };

        var messageHandler = function(event) {
            if (!event.data || event.data.type !== 'instagram_oauth_code') return;
            if (popup && !popup.closed) popup.close();

            fetchSupabaseToken()
                .then(function(token) {
                    return fetch(SUPABASE_URL + '/functions/v1/instagram-oauth', {
                        method: 'POST',
                        headers: {
                            'Authorization': 'Bearer ' + token,
                            'apikey': SUPABASE_ANON_KEY,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ code: event.data.code, redirect_uri: redirectUri }),
                    });
                })
                .then(function(res) { return res.json().then(function(b) { return { ok: res.ok, body: b }; }); })
                .then(function(result) {
                    if (!result.ok) throw new Error((result.body && result.body.error) || 'Falha ao conectar');
                    message.success('Instagram conectado com sucesso!');
                    setConnection(result.body && result.body.data);
                })
                .catch(function(err) {
                    message.error(err.message || 'Erro ao conectar o Instagram');
                })
                .finally(finish);
        };
        window.addEventListener('message', messageHandler);

        var poll = setInterval(function() {
            if (popup && popup.closed) finish();
        }, 1000);
    }, []);

    var openManual = useCallback(function() {
        setManualForm({ page_id: '', page_access_token: '', instagram_business_id: '', username: '' });
        setManualOpen(true);
    }, []);

    var updateManualField = function(field) {
        return function(e) {
            var value = e && e.target ? e.target.value : e;
            setManualForm(function(prev) {
                var next = Object.assign({}, prev);
                next[field] = value;
                return next;
            });
        };
    };

    var handleManualSubmit = useCallback(function() {
        if (!manualForm.page_id || !manualForm.page_access_token) {
            message.error('Informe o Page ID e o Page Access Token.');
            return;
        }
        setManualSubmitting(true);
        fetchSupabaseToken()
            .then(function(token) {
                if (!token) throw new Error('no-token');
                return fetch(SUPABASE_URL + '/functions/v1/instagram-manual-connect', {
                    method: 'POST',
                    headers: {
                        'Authorization': 'Bearer ' + token,
                        'apikey': SUPABASE_ANON_KEY,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        ig_business_id: manualForm.page_id.trim(),
                        access_token: manualForm.page_access_token.trim(),
                        username: manualForm.username.trim() || undefined,
                    }),
                });
            })
            .then(function(res) { return res.json().then(function(b) { return { ok: res.ok, body: b }; }); })
            .then(function(result) {
                if (!result.ok) throw new Error((result.body && result.body.error) || 'Falha ao conectar');
                message.success('Instagram conectado com sucesso!');
                setConnection(result.body && result.body.data);
                setManualOpen(false);
            })
            .catch(function(err) {
                message.error(err.message || 'Erro ao conectar o Instagram');
            })
            .finally(function() { setManualSubmitting(false); });
    }, [manualForm]);

    var handleDisconnect = useCallback(function() {
        Modal.confirm({
            title: 'Desconectar Instagram?',
            content: 'A cadência deixará de receber mensagens diretas até uma nova conexão.',
            okText: 'Desconectar',
            okType: 'danger',
            cancelText: 'Cancelar',
            zIndex: 2000,
            onOk: function() {
                return fetchSupabaseToken()
                    .then(function(token) {
                        return fetch(SUPABASE_URL + '/rest/v1/instagram_connections?is_active=eq.true', {
                            method: 'PATCH',
                            headers: {
                                'Authorization': 'Bearer ' + token,
                                'apikey': SUPABASE_ANON_KEY,
                                'Content-Type': 'application/json',
                                'Prefer': 'return=minimal',
                            },
                            body: JSON.stringify({ is_active: false }),
                        });
                    })
                    .then(function(res) {
                        if (!res.ok) throw new Error('disconnect-failed');
                        message.success('Instagram desconectado');
                        setConnection(null);
                    })
                    .catch(function() { message.error('Erro ao desconectar'); });
            },
        });
    }, []);

    if (loading) {
        return (
            <div style={{ padding: 40, textAlign: 'center' }}>
                <Spin />
            </div>
        );
    }

    var isConnected = connection && connection.username;

    return (
        <div style={{ width: '100%' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                <div style={{
                    width: 56, height: 56, borderRadius: 14,
                    background: 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 14px rgba(131, 58, 180, 0.25)',
                }}>
                    <Icon type="instagram" style={{ fontSize: 30, color: '#fff' }} />
                </div>
                <div>
                    <div style={{ fontSize: 20, fontWeight: 600, color: '#262626' }}>Mensagens do Instagram</div>
                    <div style={{ fontSize: 13, color: '#8c8c8c' }}>
                        Receba e responda mensagens diretas da sua conta Instagram Business no Social Selling e na Prospecção Ativa.
                    </div>
                </div>
            </div>

            {/* Status card */}
            <Card
                bodyStyle={{ padding: 20 }}
                style={{
                    marginBottom: 20,
                    background: isConnected ? '#f6ffed' : '#fafafa',
                    borderColor: isConnected ? '#b7eb8f' : '#e8e8e8',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    {isConnected ? (
                        <>
                            <Icon type="check-circle" theme="filled" style={{ color: '#52c41a', fontSize: 28 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#262626' }}>
                                    Conectado
                                    <Tag color="green" style={{ marginLeft: 8 }}>@{connection.username}</Tag>
                                </div>
                                <div style={{ fontSize: 12, color: '#595959', marginTop: 4 }}>
                                    Conectado {connection.subscribed_at ? moment(connection.subscribed_at).fromNow() : '-'}
                                    {connection.last_event_at && (
                                        <> • Última mensagem {moment(connection.last_event_at).fromNow()}</>
                                    )}
                                </div>
                            </div>
                            <Button type="danger" icon="disconnect" onClick={handleDisconnect}>
                                Desconectar
                            </Button>
                        </>
                    ) : (
                        <>
                            <Icon type="disconnect" style={{ color: '#bfbfbf', fontSize: 28 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#262626' }}>Não conectado</div>
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                                    Conecte uma conta Instagram Business para começar a receber DMs.
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                                <Button type="primary" icon="link" loading={connecting} onClick={handleConnect}>
                                    Conectar Instagram
                                </Button>
                                <a
                                    onClick={openManual}
                                    style={{ fontSize: 11, color: '#8c8c8c' }}
                                >
                                    ou adicionar manualmente
                                </a>
                            </div>
                        </>
                    )}
                </div>
            </Card>

            {/* Webhook status */}
            {isConnected && (
                <Card
                    title={<span><Icon type="api" style={{ marginRight: 8 }} />Status do Webhook</span>}
                    bodyStyle={{ padding: 16 }}
                    style={{ marginBottom: 16 }}
                    extra={
                        <Button size="small" icon="reload" onClick={loadStatus}>Atualizar</Button>
                    }
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <Popover
                            placement="left"
                            overlayStyle={{ maxWidth: 360 }}
                            mouseEnterDelay={0.2}
                            title={
                                <span>
                                    <Icon type="api" style={{ marginRight: 6, color: connection.subscribed_at ? '#52c41a' : '#ff4d4f' }} />
                                    Webhook {connection.subscribed_at ? 'registrado' : 'pendente'}
                                </span>
                            }
                            content={
                                <div style={{ fontSize: 12, color: '#595959', lineHeight: 1.6 }}>
                                    <p style={{ margin: 0, marginBottom: 8 }}>
                                        Esta é a "porta de entrada" que avisa o Contlabs sempre que alguém te manda uma DM no Instagram. Sem ela, as mensagens não chegariam aqui.
                                    </p>
                                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                        Cada mensagem é validada com uma assinatura segura antes de ser aceita — então você pode confiar que nada falsificado passa.
                                    </div>
                                </div>
                            }
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'help' }}>
                                <Icon
                                    type={connection.subscribed_at ? 'check-circle' : 'close-circle'}
                                    theme="filled"
                                    style={{ fontSize: 16, color: connection.subscribed_at ? '#52c41a' : '#ff4d4f' }}
                                />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 500, color: '#262626' }}>
                                        Webhook {connection.subscribed_at ? 'registrado' : 'não registrado'}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#8c8c8c', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {SUPABASE_URL + '/functions/v1/instagram-webhook'}
                                    </div>
                                </div>
                                <Icon type="question-circle" style={{ color: '#bfbfbf' }} />
                            </div>
                        </Popover>

                        <Popover
                            placement="left"
                            overlayStyle={{ maxWidth: 360 }}
                            mouseEnterDelay={0.4}
                            title={
                                <span>
                                    <Icon type={connection.last_event_at ? 'check-circle' : 'clock-circle'} style={{ marginRight: 6, color: connection.last_event_at ? '#52c41a' : '#faad14' }} />
                                    {connection.last_event_at ? 'Fluxo de DMs ativo' : 'Aguardando primeira DM'}
                                </span>
                            }
                            content={
                                <div style={{ fontSize: 12, color: '#595959', lineHeight: 1.6 }}>
                                    {connection.last_event_at ? (
                                        <>
                                            <p style={{ margin: 0, marginBottom: 8 }}>
                                                Tudo certo! As DMs do seu Instagram estão chegando aqui automaticamente. A última foi recebida <strong>{moment(connection.last_event_at).fromNow()}</strong>.
                                            </p>
                                            <div style={{ background: '#fafafa', padding: 8, borderRadius: 6, marginBottom: 6 }}>
                                                <strong style={{ fontSize: 11, color: '#262626' }}>Quando alguém te manda uma DM:</strong>
                                                <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 11 }}>
                                                    <li>A mensagem aparece imediatamente no Contlabs</li>
                                                    <li>Se o @ já estiver cadastrado em algum lead, vinculamos automaticamente</li>
                                                    <li>Se você estiver com o lead aberto, vê a mensagem em tempo real (sem precisar atualizar)</li>
                                                </ul>
                                            </div>
                                            <div style={{ fontSize: 11, color: '#1890ff' }}>
                                                <Icon type="right" style={{ marginRight: 4 }} />
                                                Clique para ver as últimas 100 mensagens recebidas.
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <p style={{ margin: 0, marginBottom: 8 }}>
                                                A integração está ativa, mas ainda não recebemos nenhuma DM. Pode ser por:
                                            </p>
                                            <ul style={{ margin: '0 0 8px 16px', padding: 0, fontSize: 11 }}>
                                                <li>Ninguém enviou DM para <strong>@{connection.username}</strong> ainda</li>
                                                <li>A permissão de receber mensagens ainda está em aprovação no Meta</li>
                                                <li>A opção "Mensagens com aplicativos" está desativada no Instagram (Configurações → Mensagens e respostas)</li>
                                            </ul>
                                            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                                Para testar, envie uma DM da sua conta pessoal para <strong>@{connection.username}</strong> e atualize esta página.
                                            </div>
                                        </>
                                    )}
                                </div>
                            }
                        >
                            <div
                                onClick={connection.last_event_at ? openMessages : undefined}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: connection.last_event_at ? '6px 8px' : 0,
                                    margin: connection.last_event_at ? '-6px -8px' : 0,
                                    borderRadius: 8,
                                    cursor: connection.last_event_at ? 'pointer' : 'help',
                                    transition: 'background 0.15s ease',
                                }}
                                onMouseEnter={function(e) {
                                    if (connection.last_event_at) e.currentTarget.style.background = '#fafafa';
                                }}
                                onMouseLeave={function(e) {
                                    if (connection.last_event_at) e.currentTarget.style.background = 'transparent';
                                }}
                            >
                                <Icon
                                    type={connection.last_event_at ? 'check-circle' : 'clock-circle'}
                                    theme="filled"
                                    style={{ fontSize: 16, color: connection.last_event_at ? '#52c41a' : '#faad14' }}
                                />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 500, color: '#262626' }}>
                                        {connection.last_event_at ? 'Recebendo mensagens' : 'Aguardando primeira mensagem'}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                        {connection.last_event_at
                                            ? 'Última DM recebida ' + moment(connection.last_event_at).fromNow() + ' • clique para ver as últimas 100'
                                            : 'Nenhuma DM recebida ainda. Envie uma mensagem para @' + connection.username + ' para testar.'}
                                    </div>
                                </div>
                                {connection.last_event_at && (
                                    <Icon type="right" style={{ fontSize: 12, color: '#bfbfbf' }} />
                                )}
                            </div>
                        </Popover>

                        {!connection.subscribed_at && (
                            <Alert
                                type="warning"
                                showIcon
                                message="Webhook pendente de aprovação"
                                description="A permissão pages_messaging ainda não foi aprovada pela Meta. As DMs não serão entregues até a aprovação do App Review."
                                style={{ marginTop: 4 }}
                            />
                        )}
                    </div>
                </Card>
            )}

            {/* Prerequisites */}
            <Card title={<span><Icon type="info-circle" style={{ marginRight: 8 }} />Pré-requisitos</span>} bodyStyle={{ padding: 16 }} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <PrereqRow icon="user" title="Conta Instagram Profissional" detail="Business ou Criador (gratuito na app do Instagram)." />
                    <PrereqRow icon="message" title="Mensagens com aplicativos ativadas" detail="No app do Instagram: Configurações → Mensagens e respostas → Mensagens com aplicativos." />
                    <PrereqRow icon="safety" title="Login com a conta principal" detail="Use a própria conta Instagram Business ao autorizar (não uma conta pessoal vinculada)." />
                </div>
            </Card>

            {/* What we access */}
            <Card title={<span><Icon type="lock" style={{ marginRight: 8 }} />O que acessamos</span>} bodyStyle={{ padding: 16 }} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: '#595959', marginBottom: 10 }}>
                    Ao conectar, autorizamos apenas o necessário para receber e responder DMs da sua conta Instagram:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {['instagram_business_basic', 'instagram_business_manage_messages'].map(function(s) {
                        return <Tag key={s} color="blue" style={{ fontFamily: 'monospace' }}>{s}</Tag>;
                    })}
                </div>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 10 }}>
                    Não acessamos posts, seguidores ou DMs de outras contas.
                </div>
            </Card>

            {/* How it works */}
            <Card title={<span><Icon type="bulb" style={{ marginRight: 8 }} />Como funciona</span>} bodyStyle={{ padding: 16 }} style={{ marginBottom: 16 }}>
                <ol style={{ margin: 0, paddingLeft: 18, color: '#595959', fontSize: 13, lineHeight: 1.8 }}>
                    <li>Clique em <strong>Conectar Instagram</strong> e entre na sua conta Instagram Business.</li>
                    <li>Autorize as permissões de mensagens.</li>
                    <li>Quando alguém enviar uma DM, um lead é criado automaticamente no Social Selling ou na Prospecção Ativa.</li>
                    <li>Abra o lead e vá até a aba <strong>Mensagens</strong> para conversar em tempo real.</li>
                </ol>
            </Card>

            <Modal
                title="Adicionar Instagram manualmente"
                visible={manualOpen}
                onCancel={function() { if (!manualSubmitting) setManualOpen(false); }}
                onOk={handleManualSubmit}
                okText="Conectar"
                cancelText="Cancelar"
                confirmLoading={manualSubmitting}
                zIndex={2000}
                destroyOnClose
            >
                <Alert
                    type="info"
                    showIcon
                    message="Use somente se você já tiver um Instagram User Access Token (IGAA…) de longa duração."
                    style={{ marginBottom: 16 }}
                />
                <Form layout="vertical">
                    <Form.Item label="Instagram Business Account ID" required>
                        <Input
                            placeholder="ex: 17841400000000000"
                            value={manualForm.page_id}
                            onChange={updateManualField('page_id')}
                            disabled={manualSubmitting}
                        />
                    </Form.Item>
                    <Form.Item label="Instagram User Access Token" required>
                        <Input.TextArea
                            rows={3}
                            placeholder="IGAA..."
                            value={manualForm.page_access_token}
                            onChange={updateManualField('page_access_token')}
                            disabled={manualSubmitting}
                            style={{ fontFamily: 'monospace', fontSize: 12 }}
                        />
                    </Form.Item>
                    <Form.Item label="Username (opcional)" extra="Detectado automaticamente se vazio.">
                        <Input
                            placeholder="ex: minhaempresa"
                            value={manualForm.username}
                            onChange={updateManualField('username')}
                            disabled={manualSubmitting}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {!META_APP_ID && (
                <Alert
                    showIcon
                    type="warning"
                    message="Configuração pendente"
                    description="A variável REACT_APP_META_APP_ID não foi definida no build. Contate o suporte para habilitar a integração."
                    style={{ marginBottom: 16 }}
                />
            )}

            <MessagesDrawer
                open={messagesOpen}
                loading={messagesLoading}
                messages={messages}
                username={connection && connection.username}
                onClose={function() { setMessagesOpen(false); }}
                onReload={loadMessages}
            />
        </div>
    );
};

// Renders the latest 100 DMs as a chat-style timeline. Inbound bubbles sit
// on the left; outbound (echoes of replies sent by the page) on the right
// in the Instagram gradient. We group consecutive same-sender messages and
// drop a date divider whenever the calendar day changes.
var MessagesDrawer = function(props) {
    var open = props.open;
    var loading = props.loading;
    var rawMessages = props.messages;
    var pageUsername = props.username;
    var onClose = props.onClose;
    var onReload = props.onReload;

    // Server returns newest first; flip to chronological for chat layout.
    var ordered = (rawMessages || []).slice().reverse();

    var renderBody = function() {
        if (loading) {
            return (
                <div style={{ padding: 60, textAlign: 'center' }}>
                    <Spin />
                </div>
            );
        }
        if (!ordered.length) {
            return (
                <div style={{ padding: 60 }}>
                    <Empty description="Nenhuma mensagem ainda" />
                </div>
            );
        }

        var lastDay = null;
        var lastSenderKey = null;
        var nodes = [];

        ordered.forEach(function(msg, idx) {
            var when = msg.received_at ? moment(msg.received_at) : null;
            var dayKey = when ? when.format('YYYY-MM-DD') : 'unknown';
            if (dayKey !== lastDay) {
                nodes.push(
                    <DateDivider key={'d-' + dayKey + '-' + idx} when={when} />
                );
                lastDay = dayKey;
                lastSenderKey = null;
            }

            var isOutbound = msg.direction === 'outbound';
            var senderKey = isOutbound ? 'me' : (msg.sender_id || msg.sender_username || 'them');
            var showHeader = senderKey !== lastSenderKey;
            lastSenderKey = senderKey;

            nodes.push(
                <MessageBubble
                    key={msg.id || idx}
                    msg={msg}
                    isOutbound={isOutbound}
                    showHeader={showHeader}
                    pageUsername={pageUsername}
                />
            );
        });

        return (
            <div style={{ padding: '12px 16px 24px' }}>
                {nodes}
            </div>
        );
    };

    return (
        <Drawer
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                        width: 32, height: 32, borderRadius: 9,
                        background: 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Icon type="instagram" style={{ fontSize: 18, color: '#fff' }} />
                    </div>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#262626', lineHeight: 1.2 }}>
                            Últimas mensagens
                        </div>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                            {pageUsername ? '@' + pageUsername : 'Instagram'} • até 100 mais recentes
                        </div>
                    </div>
                </div>
            }
            placement="right"
            width={460}
            visible={open}
            onClose={onClose}
            zIndex={1500}
            bodyStyle={{ padding: 0, background: '#fafafa' }}
            extra={
                <Button size="small" icon="reload" onClick={onReload} disabled={loading}>
                    Atualizar
                </Button>
            }
        >
            {renderBody()}
        </Drawer>
    );
};

var DateDivider = function(props) {
    var when = props.when;
    var label = when ? formatDayLabel(when) : '—';
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0 12px' }}>
            <div style={{ flex: 1, height: 1, background: '#e8e8e8' }} />
            <div style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
                {label}
            </div>
            <div style={{ flex: 1, height: 1, background: '#e8e8e8' }} />
        </div>
    );
};

var formatDayLabel = function(m) {
    var today = moment().startOf('day');
    var yest = moment().subtract(1, 'day').startOf('day');
    var d = m.clone().startOf('day');
    if (d.isSame(today)) return 'Hoje';
    if (d.isSame(yest)) return 'Ontem';
    if (m.isAfter(moment().subtract(7, 'days'))) return m.format('dddd');
    return m.format('DD [de] MMMM');
};

var MessageBubble = function(props) {
    var msg = props.msg;
    var isOutbound = props.isOutbound;
    var showHeader = props.showHeader;
    var pageUsername = props.pageUsername;

    var senderLabel = isOutbound
        ? (pageUsername ? '@' + pageUsername : 'Você')
        : (msg.sender_username ? '@' + msg.sender_username : 'Anônimo');

    var initials = (function() {
        var src = isOutbound ? (pageUsername || 'EU') : (msg.sender_username || '??');
        return src.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase() || '??';
    })();

    var bubbleStyle = isOutbound
        ? {
            background: 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)',
            color: '#fff',
            borderRadius: '16px 16px 4px 16px',
        }
        : {
            background: '#fff',
            color: '#262626',
            border: '1px solid #f0f0f0',
            borderRadius: '16px 16px 16px 4px',
        };

    var hasText = !!(msg.text && msg.text.length);
    var attachments = msg.attachments;
    var attachmentCount = (attachments && attachments.length) || 0;

    return (
        <div style={{
            display: 'flex',
            justifyContent: isOutbound ? 'flex-end' : 'flex-start',
            marginBottom: showHeader ? 8 : 2,
            gap: 8,
        }}>
            {!isOutbound && (
                <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: showHeader ? 'linear-gradient(135deg, #fd1d1d 0%, #fcb045 100%)' : 'transparent',
                    color: '#fff', fontSize: 11, fontWeight: 600,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, alignSelf: 'flex-end',
                    visibility: showHeader ? 'visible' : 'hidden',
                }}>
                    {initials}
                </div>
            )}

            <div style={{ maxWidth: '78%', display: 'flex', flexDirection: 'column', alignItems: isOutbound ? 'flex-end' : 'flex-start' }}>
                {showHeader && (
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 3, padding: '0 4px' }}>
                        {senderLabel}
                    </div>
                )}
                <div style={Object.assign({
                    padding: '8px 12px',
                    fontSize: 13,
                    lineHeight: 1.45,
                    wordBreak: 'break-word',
                    boxShadow: isOutbound ? '0 1px 4px rgba(131, 58, 180, 0.18)' : '0 1px 2px rgba(0, 0, 0, 0.03)',
                }, bubbleStyle)}>
                    {hasText && (
                        <div style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div>
                    )}
                    {attachmentCount > 0 && (
                        <div style={{
                            marginTop: hasText ? 6 : 0,
                            fontSize: 12,
                            opacity: 0.85,
                            display: 'flex', alignItems: 'center', gap: 6,
                        }}>
                            <Icon type="paper-clip" />
                            {attachmentCount} {attachmentCount === 1 ? 'anexo' : 'anexos'}
                        </div>
                    )}
                    {!hasText && attachmentCount === 0 && (
                        <div style={{ fontStyle: 'italic', opacity: 0.7, fontSize: 12 }}>
                            (sem conteúdo)
                        </div>
                    )}
                </div>
                <div style={{ fontSize: 10, color: '#bfbfbf', marginTop: 3, padding: '0 4px' }}>
                    {msg.received_at ? moment(msg.received_at).format('HH:mm') : ''}
                </div>
            </div>

            {isOutbound && (
                <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: showHeader ? 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 100%)' : 'transparent',
                    color: '#fff', fontSize: 11, fontWeight: 600,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, alignSelf: 'flex-end',
                    visibility: showHeader ? 'visible' : 'hidden',
                }}>
                    {initials}
                </div>
            )}
        </div>
    );
};

var PrereqRow = function(props) {
    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e6f7ff', color: '#1890ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon type={props.icon} />
            </div>
            <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#262626' }}>{props.title}</div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>{props.detail}</div>
            </div>
        </div>
    );
};

export default InstagramIntegration;
